import React, { useState } from "react";
import api, { apiError } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardCheck, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { compressImage, readAsDataUrl } from "@/lib/image";

export const VISIT_KINDS = ["زيارة ميدانية", "اتصال هاتفي", "رسالة للمنسق", "اجتماع"];
const STATUSES = ["مفعّلة", "تحتاج دعم", "متعثرة"];
const inputCls = "w-full px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-500";
const today = () => new Date().toISOString().slice(0, 10);

export const VisitDialog = ({ schools, defaultSchoolId, onClose, onSaved }) => {
  const [f, setF] = useState({ school_id: defaultSchoolId || "", kind: VISIT_KINDS[0], status: "", notes: "", date: today(), attachment: "" });
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      if (file.type === "application/pdf") {
        if (file.size > 3 * 1024 * 1024) throw new Error("ملف PDF أكبر من 3 ميجابايت");
        set("attachment", await readAsDataUrl(file));
      } else if (file.type.startsWith("image/")) {
        set("attachment", await compressImage(file));
      } else throw new Error("الصيغ المقبولة: صورة أو PDF");
      setFileName(file.name);
    } catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    if (!f.school_id) return toast.error("اختر المدرسة");
    setSaving(true);
    try {
      await api.post("/my-visits", f);
      toast.success("تم حفظ المتابعة");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-lg max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="visit-dialog">
        <DialogHeader><DialogTitle className="gold-text font-head flex items-center gap-2"><ClipboardCheck size={18} /> تسجيل متابعة</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select data-testid="visit-school" value={f.school_id} onChange={(e) => set("school_id", e.target.value)} className={inputCls}>
            <option value="">اختر المدرسة...</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="flex flex-wrap gap-1.5">
            {VISIT_KINDS.map((k) => (
              <button key={k} type="button" data-testid={`visit-kind-${k}`} onClick={() => set("kind", k)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.kind === k ? "bg-sky-600 text-white border-sky-600" : "bg-white border-sky-300/50 text-stone-700"}`}>{k}</button>
            ))}
          </div>
          <div>
            <div className="text-xs text-stone-500 mb-1">حالة التفعيل</div>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button key={s} type="button" data-testid={`visit-status-${s}`} onClick={() => set("status", f.status === s ? "" : s)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.status === s ? "bg-amber-600 text-white border-amber-600" : "bg-white border-amber-400/40 text-stone-700"}`}>{s}</button>
              ))}
            </div>
          </div>
          <input data-testid="visit-date" type="date" value={f.date} max={today()} onChange={(e) => set("date", e.target.value)} className={inputCls} />
          <textarea data-testid="visit-notes" rows={4} maxLength={3000} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="الملاحظات والتوصيات..." className={inputCls} />
          <div className="flex items-center gap-2">
            <label className="px-3 py-2 rounded-xl border border-dashed border-amber-400/60 text-sm text-stone-600 cursor-pointer flex items-center gap-2 hover:bg-amber-50">
              <Paperclip size={15} /> إرفاق تقرير (صورة أو PDF)
              <input data-testid="visit-file" type="file" accept="image/*,application/pdf" className="hidden" onChange={pickFile} />
            </label>
            {f.attachment && <span className="text-xs text-emerald-700 flex items-center gap-1 truncate">{fileName}
              <button type="button" data-testid="visit-file-remove" onClick={() => { set("attachment", ""); setFileName(""); }}><X size={14} /></button></span>}
          </div>
          <button data-testid="visit-save" onClick={save} disabled={saving} className="btn-gold w-full py-2.5 rounded-xl disabled:opacity-60">{saving ? "جارٍ الحفظ..." : "حفظ المتابعة"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisitDialog;
